import { isSectionSpecPdf } from "@/shared/models/SectionSpecUpload";

export const SECTION_SPEC_ROTATION = {
	LEFT: -90,
	RIGHT: 90,
} as const;

export type SectionSpecRotation =
	(typeof SECTION_SPEC_ROTATION)[keyof typeof SECTION_SPEC_ROTATION];

export interface SectionSpecFileChange {
	source: File | null;
	angle: number;
	file: File;
}

const PDF_MEDIA_TYPE = "application/pdf";
const PNG_MEDIA_TYPE = "image/png";
const REENCODABLE_IMAGE_TYPES = new Set([
	"image/jpeg",
	"image/png",
	"image/webp",
]);

export const getSectionSpecRotationAngle = (change: SectionSpecFileChange) =>
	change.source === null ? change.angle : null;

export const normalizeSectionSpecAngle = (angle: number) =>
	((angle % 360) + 360) % 360;

export const getRotatedAspectRatio = (
	aspectRatio: number | null,
	change: SectionSpecFileChange | null,
) =>
	aspectRatio !== null && change !== null && change.angle % 180 !== 0
		? 1 / aspectRatio
		: aspectRatio;

const loadImage = (blob: Blob) =>
	new Promise<HTMLImageElement>((resolve, reject) => {
		const url = URL.createObjectURL(blob);
		const image = new Image();
		image.onload = () => {
			URL.revokeObjectURL(url);
			resolve(image);
		};
		image.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error("The image could not be read"));
		};
		image.src = url;
	});

const rotateImage = async (
	blob: Blob,
	fileName: string,
	angle: number,
): Promise<File> => {
	const image = await loadImage(blob);
	const quarterTurn = angle % 180 !== 0;
	const canvas = document.createElement("canvas");
	canvas.width = quarterTurn ? image.naturalHeight : image.naturalWidth;
	canvas.height = quarterTurn ? image.naturalWidth : image.naturalHeight;
	const context = canvas.getContext("2d");
	if (!context) throw new Error("The image could not be rotated");

	context.translate(canvas.width / 2, canvas.height / 2);
	context.rotate((angle * Math.PI) / 180);
	context.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

	const type = REENCODABLE_IMAGE_TYPES.has(blob.type)
		? blob.type
		: PNG_MEDIA_TYPE;
	const rotated = await new Promise<Blob | null>((resolve) =>
		canvas.toBlob(resolve, type, 0.95),
	);
	if (!rotated) throw new Error("The image could not be rotated");

	const name =
		type === blob.type ? fileName : fileName.replace(/\.[^./\\]*$/, ".png");
	return new File([rotated], name, { type });
};

const rotatePdf = async (
	blob: Blob,
	fileName: string,
	angle: number,
): Promise<File> => {
	const { PDFDocument, degrees } = await import("pdf-lib");
	const pdf = await PDFDocument.load(await blob.arrayBuffer());
	for (const page of pdf.getPages()) {
		page.setRotation(
			degrees(normalizeSectionSpecAngle(page.getRotation().angle + angle)),
		);
	}
	const bytes = new Uint8Array(await pdf.save());
	return new File([bytes], fileName, { type: PDF_MEDIA_TYPE });
};

export const rotateSectionSpecBlob = (
	blob: Blob,
	fileName: string,
	angle: number,
): Promise<File> =>
	isSectionSpecPdf(fileName, blob.type)
		? rotatePdf(blob, fileName, angle)
		: rotateImage(blob, fileName, angle);

export const applySectionSpecRotation = async (
	change: SectionSpecFileChange | null,
	persisted: { blob: Blob; fileName: string } | null,
	rotation: SectionSpecRotation,
): Promise<SectionSpecFileChange | null> => {
	const source = change?.source ?? null;
	const angle = normalizeSectionSpecAngle((change?.angle ?? 0) + rotation);
	const base = source ?? persisted?.blob;
	const fileName = source?.name ?? persisted?.fileName;
	if (!base || !fileName) return change;
	if (angle === 0)
		return source === null ? null : { source, angle, file: source };
	return {
		source,
		angle,
		file: await rotateSectionSpecBlob(base, fileName, angle),
	};
};
